import { useMemo, useRef, useState } from 'react';
import {
  UMBuildingDetailPanel, UMExportPanel, UMFiltersPanel, UMIcon, UMImportPanel, UMMap, UMMapControls, UMMapToolbar, UMMeasuresPanel, UMR, UMSelectionPanel,
  UM_DEMO_BUILDING, type UMBuildingDetail, type UMFilterValues, type UMMapHandle, type UMRailItemId,
} from '../../urbanmap/_components';
import { planLayers, planSources, type PlanLayerVisibility } from './mapLayers';
import { EditPanel, PlanRail, ProjectLayersPanel, ProvenanceBanner } from './PlanPanels';
import { PLAN } from './tokens';
import type { PlanBootstrap, PlanProject } from './types';
import { fmt } from './ui';

export interface ScenarioOverlay {
  id: string;
  name: string;
  connected: Set<string>;
}

interface Props {
  data: PlanBootstrap;
  project: PlanProject;
  overrides: Record<string, number>;
  onOverride: (fid: string, heat: number | null) => void;
  visibility: PlanLayerVisibility;
  onVisibilityChange: (v: PlanLayerVisibility) => void;
  scenario: ScenarioOverlay | null;
  onClearScenario: () => void;
  onBackToStudy: () => void;
  flash: (msg: string) => void;
  showOnboarding: boolean;
  onDismissOnboarding: () => void;
}

const BDNB = 'BDNB (DataFrance v_6_1_0_6)';
const DPE = 'DPE ADEME';

/** Maps the real UrbanMap building record (copied into the project) to the UM detail card. */
function toDetail(fid: string, heat: number, p: Record<string, unknown>): UMBuildingDetail {
  const heating = Math.round(heat * 0.8 * 10) / 10;
  const str = (k: string, fallback = 'Inconnu') => (p[k] as string | undefined) ?? fallback;
  const share = (p.usageShare as number) ?? 100;
  const usages = [{ label: str('usageLabel'), share }];
  if (p.usageSecondary && share < 100) usages.push({ label: p.usageSecondary as string, share: 100 - share });
  const mode = (k: string) => { const v = p[k]; return v === 'Individuel' || v === 'Collectif' ? v : 'Inconnu'; };
  const ownerType = p.ownerType === 'Public' || p.ownerType === 'Privé' ? p.ownerType : 'Inconnu';
  return {
    ...UM_DEMO_BUILDING,
    functionalId: fid,
    address: str('address', 'Adresse inconnue'),
    selections: [],
    heating: { energy: { value: str('heatingEnergy'), source: DPE }, mode: { value: mode('heatingMode'), source: BDNB }, consumption: heating },
    hotWater: { energy: { value: str('ecsEnergy'), source: DPE }, mode: { value: mode('ecsMode'), source: BDNB }, consumption: Math.round((heat - heating) * 10) / 10 },
    cooling: 0,
    usages: { value: usages, source: `${BDNB}, Surface au sol` },
    totalArea: { value: (p.totalArea as number) ?? 0, source: BDNB },
    storeys: { value: (p.storeys as number) ?? 0, source: BDNB },
    dwellings: { value: Number(p.dwellings ?? 0), source: BDNB },
    constructionYear: { value: (p.year as number) ?? 0, source: BDNB },
    owner: { value: str('owner', 'Non renseigné'), source: BDNB },
    ownerType: { value: ownerType, source: BDNB },
    syndic: { value: str('syndic', 'Non renseigné'), source: BDNB },
    dataVersion: 'v_6_1_0_6',
  };
}

export function PlanMapView({
  data, project, overrides, onOverride, visibility, onVisibilityChange, scenario, onClearScenario, onBackToStudy, flash, showOnboarding, onDismissOnboarding,
}: Props) {
  const mapRef = useRef<UMMapHandle>(null);
  const [panel, setPanel] = useState<UMRailItemId | null>(null);
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState('');
  const [filters, setFilters] = useState<UMFilterValues>({});
  const [tool, setTool] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const byFid = useMemo(
    () => Object.fromEntries(data.geo.buildings.features.map((f) => [f.properties?.functionalId as string, f])),
    [data.geo.buildings],
  );
  const original = useMemo(
    () => Object.fromEntries(Object.entries(byFid).map(([k, f]) => [k, f.properties?.heat as number])),
    [byFid],
  );
  const driftMap = useMemo(() => Object.fromEntries(project.umDrift.map((d) => [d.functionalId, d.change])), [project.umDrift]);
  const driftSet = useMemo(() => new Set(Object.keys(driftMap)), [driftMap]);
  const inside = useMemo(() => data.geo.buildings.features.filter((f) => f.properties?.inside === 1), [data.geo.buildings]);

  const sources = useMemo(
    () => planSources(data.geo, { modified: overrides, drift: driftSet, connected: scenario?.connected ?? new Set() }),
    [data.geo, overrides, driftSet, scenario],
  );
  const pickedNum = picked ? (byFid[picked]?.properties?.id as number) : null;
  const layers = useMemo(() => planLayers(visibility, { pickedId: pickedNum, scenarioShown: !!scenario }), [visibility, pickedNum, scenario]);

  const close = () => { setPanel(null); setTool(null); };
  const toggle = (id: UMRailItemId) => { setTool(null); setPanel((c) => (c === id ? null : id)); };
  const select = (fid: string) => { setPicked(fid); setEditing(false); };

  const pickedFeature = picked ? byFid[picked] : null;
  const pickedHeat = picked ? overrides[picked] ?? original[picked] : 0;
  const folderIds = folder === 'Gros consommateurs (> 1 GWh)' ? inside.filter((f) => (f.properties?.heat as number) > 1000) : folder ? inside : [];

  const leftPanel =
    (panel === 'layers' && (
      <ProjectLayersPanel
        project={project}
        visibility={visibility}
        onChange={onVisibilityChange}
        modifiedCount={Object.keys(overrides).length}
        insideCount={inside.length}
        onClose={close}
        scenarioName={scenario?.name}
      />
    )) ||
    (panel === 'edit' && (
      <EditPanel onClose={close} overrides={overrides} original={original} onRevert={(fid) => onOverride(fid, null)} onSelect={select} hasBuilding={!!picked} />
    )) ||
    (panel === 'measures' && <UMMeasuresPanel onClose={close} activeTool={tool} onToolChange={(t) => setTool((c) => (c === t ? null : t))} />) ||
    (panel === 'filters' && <UMFiltersPanel onClose={close} values={filters} onChange={setFilters} />) ||
    (panel === 'selection' && (
      <UMSelectionPanel
        onClose={close}
        activeTool={tool}
        onToolChange={(t) => setTool((c) => (c === t ? null : t))}
        summary={{
          buildings: (folder ? folderIds : inside).length,
          heatTotal: (folder ? folderIds : inside).reduce((s, f) => s + (overrides[f.properties?.functionalId as string] ?? (f.properties?.heat as number)), 0),
          pipes: Math.round(data.geo.pipes.features.reduce((s, f) => s + ((f.properties?.lengthKm as number) ?? 0), 0) * 1000),
        }}
      />
    )) ||
    (panel === 'export' && <UMExportPanel selectionLabel={project.name} onClose={close} onExport={(r) => flash(`Export ${r.format.toUpperCase()} des données du projet lancé (mock)`)} onHistory={() => flash('Historique des exports (mock)')} />) ||
    (panel === 'import' && (
      <UMImportPanel
        onClose={close}
        onImport={(f) => flash(`Import de ${f.name} dans le projet (UrbanMap n’est pas modifié) — mock`)}
        onHistory={() => flash('Historique des imports (mock)')}
        onDownloadTemplate={() => flash('Téléchargement du modèle (mock)')}
      />
    )) ||
    null;

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
      <PlanRail activeId={panel} onSelect={toggle} />
      <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 20, pointerEvents: 'none', display: 'flex' }}>
          <UMMapToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Rechercher dans le projet"
            selections={['Gros consommateurs (> 1 GWh)', 'Copropriétés à démarcher']}
            selectionLabel={folder}
            onSelectionChange={setFolder}
            onCreateFolder={() => flash('Création d’une sélection du projet (mock)')}
            onCenterSelection={() => mapRef.current?.fitBounds(data.geo.bounds)}
            onSelectionActions={() => flash('Actions de la sélection (mock)')}
            onLocate={() => mapRef.current?.fitBounds(data.geo.bounds)}
          />
        </div>
        {leftPanel && (
          <div style={{ marginTop: UMR.size.toolbarHeight, height: `calc(100% - ${UMR.size.toolbarHeight}px)`, zIndex: 15 }}>{leftPanel}</div>
        )}
        <main style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <UMMap
            ref={mapRef}
            sources={sources}
            layers={layers}
            initialBounds={data.geo.bounds}
            interactiveLayerIds={['plan-building-fill']}
            onFeatureClick={(_, f) => select(String(f.properties?.functionalId))}
            onMapClick={() => setPicked(null)}
          />
          <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />

          {scenario && (
            <div
              style={{
                position: 'absolute', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 18, display: 'flex', alignItems: 'center', gap: 10,
                background: UMR.white, borderLeft: `4px solid ${PLAN.map.scenario}`, boxShadow: '0 2px 10px rgba(0,0,0,0.15)', padding: '8px 12px', fontFamily: UMR.font, fontSize: 13,
              }}
            >
              <UMIcon name="insights" size={18} color={PLAN.map.scenario} />
              <span>Scénario affiché : <strong>{scenario.name}</strong> · {scenario.connected.size} bâtiments raccordés</span>
              <button type="button" onClick={onBackToStudy} style={{ border: 'none', background: 'transparent', color: UMR.blueLink, cursor: 'pointer', fontSize: 13 }}>Retour à l’étude</button>
              <button type="button" aria-label="Masquer le scénario" onClick={onClearScenario} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
                <UMIcon name="close" family="icons" size={18} color={UMR.textSecondary} />
              </button>
            </div>
          )}

          {showOnboarding && (
            <div
              role="note"
              style={{
                position: 'absolute', left: 16, bottom: 16, zIndex: 18, width: 380, background: UMR.white, fontFamily: UMR.font, fontSize: 13, lineHeight: '19px',
                borderTop: `4px solid ${PLAN.accent}`, boxShadow: '0 4px 16px rgba(0,0,0,0.18)', padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 15 }}>Vous travaillez sur une copie</div>
              <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <li><strong>{fmt(inside.length)} bâtiments</strong>, la voirie et les réseaux du périmètre ont été copiés le {project.snapshotDate} : vous pouvez les modifier librement.</li>
                <li>Seuls les bâtiments du périmètre (pointillés) sont affichés. Les <strong>couches de référence</strong> restent en direct et ne sont pas limitées au périmètre.</li>
                <li>Passez dans <strong>Étude</strong> pour définir les hypothèses et lancer les scénarios.</li>
              </ul>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" onClick={onDismissOnboarding} style={{ border: 'none', background: PLAN.accent, color: UMR.white, padding: '6px 14px', cursor: 'pointer', fontFamily: UMR.font, fontSize: 13 }}>
                  Compris
                </button>
              </div>
            </div>
          )}
        </main>
        {picked && pickedFeature && (
          <div style={{ position: 'absolute', top: 0, right: 0, height: '100%', zIndex: 25, display: 'flex', flexDirection: 'column', width: 300, background: UMR.white, borderLeft: `1px solid ${UMR.border}` }}>
            <ProvenanceBanner
              key={picked + String(editing)}
              project={project}
              fid={picked}
              originalHeat={original[picked]}
              modifiedHeat={overrides[picked]}
              drift={driftMap[picked]}
              editing={editing}
              onSave={(heat) => { onOverride(picked, heat); setEditing(false); flash(`${picked} modifié dans le projet — UrbanMap inchangé`); }}
              onCancelEdit={() => setEditing(false)}
              onRevert={() => onOverride(picked, null)}
              onOpenUM={() => flash(`Ouverture de ${picked} dans UrbanMap (nouvel onglet, données en direct) — mock`)}
            />
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <UMBuildingDetailPanel
                building={toDetail(picked, pickedHeat, pickedFeature.properties ?? {})}
                onClose={() => setPicked(null)}
                onEdit={() => setEditing(true)}
                onStreetView={() => flash('Street View (mock)')}
                onEarth={() => flash('Google Earth (mock)')}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
